/**
 * P0 Polish · Refunds on this Order (2026-02-16)
 *
 * Compact read-only card that surfaces every append-only adjustment row
 * for a single sticker order. Uses the existing
 *   GET /admin/sales/adjustments/list?source_type=…&source_order_id=…
 * (extended with a per-order filter in the same task) and the existing
 *   GET /admin/sales/adjustments/order-preview
 * so refunds never diverge from the reporting ledger's authoritative math.
 *
 * Reversed adjustments STAY visible with a REVERSED badge. Total is
 * NET of reversals — never a raw sum of every ledger row.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const centsFmt = (c) =>
  (Number(c || 0) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const REVERSAL_TYPE = "adjustment_reversal";
const PLAIN_TYPE_LABEL = {
  full_refund: "Full refund",
  partial_refund: "Partial refund",
  merchandise_refund: "Merchandise refund",
  tax_refund: "Tax refund",
  shipping_refund: "Shipping refund",
  item_quantity_refund: "Item/quantity refund",
  discount: "Discount",
  manual_financial_correction: "Manual correction",
  adjustment_reversal: "Reversal",
};

export function RefundHistoryCard({ order, refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [listRes, prevRes] = await Promise.all([
        api.get(
          `/admin/sales/adjustments/list?preset=this_year&source_type=sticker_order` +
            `&source_order_id=${encodeURIComponent(order.id)}&limit=200`,
        ),
        api.get(
          `/admin/sales/adjustments/order-preview` +
            `?source_type=sticker_order&source_order_id=${encodeURIComponent(order.id)}`,
        ),
      ]);
      setRows(listRes.data?.rows || []);
      setPreview(prevRes.data || null);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load refund history.");
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => { if (order?.id) load(); }, [order?.id, load, refreshKey]);

  // Mark rows whose adjustment_id is referenced by a reversal as REVERSED.
  const reversedIds = new Set(
    (rows || [])
      .filter((r) => r.adjustment_type === REVERSAL_TYPE)
      .map((r) => r.reversal_of_adjustment_id)
      .filter(Boolean),
  );

  const netRefundedCents = (rows || []).reduce(
    (acc, r) =>
      r.adjustment_type === REVERSAL_TYPE
        ? acc + Number(r.total_adjustment_cents || 0) // reversals are already sign-flipped
        : reversedIds.has(r.adjustment_id)
          ? acc + Number(r.total_adjustment_cents || 0) - Number(r.total_adjustment_cents || 0)
          : acc + Number(r.total_adjustment_cents || 0),
    0,
  );
  // The above collapses to sum(all rows) because reversal rows carry a
  // sign-flipped total_adjustment_cents that cancels the original —
  // that's the authoritative append-only invariant. Keeping the explicit
  // per-row form for readability.

  const remainingRefundableCents = Number(
    preview?.remaining?.customer_total_remaining_refundable || 0,
  );

  return (
    <section
      className="rounded-xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
      data-testid="refund-history-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-base">Refunds on this Order</h3>
        <button
          className="text-[10px] uppercase font-bold text-[var(--smoke)] hover:text-[var(--ink)]"
          onClick={load}
          disabled={loading}
          data-testid="refund-history-refresh"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-baseline">
        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">Total refunded</div>
          <div className="font-mono text-lg" data-testid="refund-history-total">{centsFmt(Math.max(0, netRefundedCents))}</div>
        </div>
        {preview && (
          <div>
            <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">Remaining refundable</div>
            <div className="font-mono text-lg" data-testid="refund-history-remaining">{centsFmt(remainingRefundableCents)}</div>
          </div>
        )}
      </div>

      {err && (
        <div className="text-xs text-red-800" data-testid="refund-history-error">{err}</div>
      )}

      {loading && !rows && (
        <div className="text-xs text-[var(--smoke)]">Loading refund history…</div>
      )}

      {rows && rows.length === 0 && (
        <div className="text-xs text-[var(--smoke)] italic" data-testid="refund-history-empty">
          No refunds recorded for this order.
        </div>
      )}

      {rows && rows.length > 0 && (
        <ul className="space-y-2" data-testid="refund-history-list">
          {rows.map((r) => {
            const isReversal = r.adjustment_type === REVERSAL_TYPE;
            const isReversed = reversedIds.has(r.adjustment_id);
            const label = PLAIN_TYPE_LABEL[r.adjustment_type] || r.adjustment_type;
            return (
              <li
                key={r.adjustment_id}
                className={`border rounded-md p-2 text-xs ${isReversed ? "border-neutral-300 bg-neutral-50 opacity-70" : "border-neutral-200"}`}
                data-testid={`refund-history-row-${r.adjustment_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-mono text-[11px]">
                    {new Date(r.effective_at).toLocaleString()}
                  </div>
                  <div className="flex gap-1">
                    {isReversal && (
                      <span className="uppercase text-[9px] font-bold bg-orange-100 text-orange-800 border border-orange-400 px-1 rounded"
                            data-testid="refund-history-reversal-badge">
                        REVERSAL
                      </span>
                    )}
                    {isReversed && (
                      <span className="uppercase text-[9px] font-bold bg-red-100 text-red-800 border border-red-400 px-1 rounded"
                            data-testid="refund-history-reversed-badge">
                        REVERSED
                      </span>
                    )}
                    {!isReversal && !isReversed && (
                      <span className="uppercase text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-400 px-1 rounded">
                        ACTIVE
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1">
                  <div>
                    <div className="text-[9px] uppercase text-[var(--smoke)]">Type</div>
                    <div className="font-mono">{label}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-[var(--smoke)]">Amount</div>
                    <div className="font-mono">
                      {isReversal ? "+" : "−"}
                      {centsFmt(Math.abs(Number(r.total_adjustment_cents || 0)))}
                    </div>
                  </div>
                  {r.refund_quantity ? (
                    <div>
                      <div className="text-[9px] uppercase text-[var(--smoke)]">Qty · Line</div>
                      <div className="font-mono">
                        {r.refund_quantity} · <span className="font-bold">{r.line_item_reference || "—"}</span>
                      </div>
                    </div>
                  ) : r.line_item_reference ? (
                    <div>
                      <div className="text-[9px] uppercase text-[var(--smoke)]">Line</div>
                      <div className="font-mono font-bold">{r.line_item_reference}</div>
                    </div>
                  ) : (
                    <div />
                  )}
                  <div>
                    <div className="text-[9px] uppercase text-[var(--smoke)]">Return method</div>
                    <div className="font-mono">{r.payment_method || "—"}</div>
                  </div>
                </div>
                {r.reason && (
                  <div className="mt-1 text-[11px] text-[var(--smoke)]" data-testid={`refund-history-reason-${r.adjustment_id}`}>
                    Reason: {r.reason}
                  </div>
                )}
                <div className="mt-1 text-[10px] text-[var(--smoke)]">
                  Admin: {r.admin_display_name || "system"} · <span className="font-mono">{r.adjustment_id.slice(0, 8)}…</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="text-[10px] text-[var(--smoke)] italic">
        Refunds are append-only. Reversed entries stay in the ledger for audit and no longer count toward the total.
      </div>
    </section>
  );
}


/**
 * Small button that downloads the existing /admin/sales/csv/refunds
 * export honouring the current preset + start/end passed in props.
 */
export function ExportRefundsCsvButton({ preset = "this_year", start, end, className = "" }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      let url = `/admin/sales/csv/refunds?preset=${encodeURIComponent(preset)}`;
      if (start) url += `&start=${encodeURIComponent(start)}`;
      if (end)   url += `&end=${encodeURIComponent(end)}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8;" });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `refunds-${preset}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
      toast.success("Refunds CSV downloaded.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to download refunds CSV.");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={download}
      disabled={busy}
      className={`text-xs uppercase font-bold border-2 border-[var(--ink)] rounded-full px-3 py-1 hover:bg-[var(--ink)] hover:text-white transition ${className}`}
      data-testid="export-refunds-csv"
    >
      {busy ? "Downloading…" : "Export Refunds CSV"}
    </button>
  );
}
